import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Sparkles, X, ArrowRight, Gift } from "lucide-react";

/**
 * DemoLockModal - Shows when demo session expires (3 min or 2 features)
 */
export default function DemoLockModal({ isOpen, lockReason, onClose }) {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");

  if (!isOpen) return null;

  const getMessage = () => {
    if (lockReason === "time_limit") {
      return {
        icon: <Clock className="w-12 h-12 text-indigo-400" />,
        title: "Demo Time's Up!",
        description: "You've explored ClientKit for 3 minutes. Ready to unlock all features?"
      };
    }
    return {
      icon: <Sparkles className="w-12 h-12 text-indigo-400" />,
      title: "Demo Features Used",
      description: "You've tried 2 premium features. Sign up to keep using them!"
    };
  };

  const { icon, title, description } = getMessage();

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError("");
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promo/validate/${promoCode}`);
      const data = await res.json();
      
      if (data.valid) {
        // Store promo code for after signup
        localStorage.setItem("pendingPromoCode", promoCode);
        navigate("/signup?promo=" + promoCode);
      } else {
        setPromoError(data.error || "Invalid promo code");
      }
    } catch (e) {
      setPromoError("Failed to validate code");
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-indigo-500 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-slate-400/50 flex items-center justify-center mx-auto mb-4">
            {icon}
          </div>
          <h2 className="text-2xl font-bold text-indigo-500 mb-2">{title}</h2>
          <p className="text-indigo-400">{description}</p>
        </div>

        {/* Thank you message */}
        <div className="bg-gradient-to-r from-indigo-500/10 to-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 mb-6">
          <p className="text-sm text-indigo-400 text-center">
            Thank you for trying ClientKit! We hope you enjoyed exploring our freelancer tools.
          </p>
        </div>

        {/* Promo code section */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-indigo-400 mb-2">
            <Gift className="w-4 h-4 text-indigo-400" />
            Have a promo code?
          </div>
          <form onSubmit={handlePromoSubmit} className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none uppercase"
            />
            <button
              type="submit"
              disabled={promoLoading || !promoCode.trim()}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {promoLoading ? "..." : "Apply"}
            </button>
          </form>
          {promoError && (
            <p className="text-red-400 text-xs mt-2">{promoError}</p>
          )}
        </div>

        {/* CTA buttons */}
        <div className="space-y-3">
          <Link
            to="/pricing"
            className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl transition-all"
          >
            View Pricing & Sign Up
            <ArrowRight className="w-5 h-5" />
          </Link>
          
          <button
            onClick={onClose}
            className="w-full py-2 text-indigo-400 hover:text-indigo-500 transition-colors text-sm"
          >
            Continue exploring (limited)
          </button>
        </div>

        {/* Features preview */}
        <div className="mt-6 pt-6 border-t border-slate-700">
          <p className="text-xs text-slate-400 text-center mb-3">What you'll get with Pro:</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-indigo-400">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              Contract Builder
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              Proof Builder
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              Scope Guard
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              Unlimited Clients
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
